import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  WakeDetailsData,
  wakeDetailsService,
} from '../../../../services/wakeDetailsInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('WakeDetailsData');

type RouteParams = {
  wakeDetailsId?: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type WakeDetailsDataScreenRouteProp = RouteProp<RootStackParamList, 'WakeDetailsData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const WakeDetailsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<WakeDetailsDataScreenRouteProp>();
  const [wakeDetailsInfo, setWakeDetailsInfo] = useState<WakeDetailsData | null>(null);
  const [loading, setLoading] = useState(true);

  const wakeDetailsId = route.params?.wakeDetailsId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly;

  useEffect(() => {
    loadWakeDetailsInfo();
  }, []);

  const loadWakeDetailsInfo = async () => {
    try {
      if (wakeDetailsId) {
        const info = await wakeDetailsService.getWakeDetailsById(wakeDetailsId, targetUserId);
        setWakeDetailsInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await wakeDetailsService.getWakeDetails(targetUserId);
        if (allInfo.length > 0) {
          setWakeDetailsInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading wake details info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (wakeDetailsInfo) {
      // Map the data from wakeDetailsInfo to the format expected by WakeDetailsInputScreen
      const mappedData = {
        id: wakeDetailsInfo.id,
        wakeType: wakeDetailsInfo.wakeType,
        wakeLocationPreference: wakeDetailsInfo.wakeLocationPreference,
        wakeLocation: wakeDetailsInfo.wakeLocation,
        casketDisplayPreference: wakeDetailsInfo.casketDisplayPreference,
        casketDisplay: wakeDetailsInfo.casketDisplay,
        shareMemories: wakeDetailsInfo.shareMemories,
        favoriteOutfitPreference: wakeDetailsInfo.favoriteOutfitPreference,
        favoriteOutfit: wakeDetailsInfo.favoriteOutfit,
        favoritePhotosPreference: wakeDetailsInfo.favoritePhotosPreference,
        favoritePhotos: wakeDetailsInfo.favoritePhotos,
        favoriteArtPreference: wakeDetailsInfo.favoriteArtPreference,
        favoriteArt: wakeDetailsInfo.favoriteArt,
        favoriteVideosPreference: wakeDetailsInfo.favoriteVideosPreference,
        favoriteVideos: wakeDetailsInfo.favoriteVideos,
        readingsPreference: wakeDetailsInfo.readingsPreference,
        readingsName: wakeDetailsInfo.readingsName,
        specialRequests: wakeDetailsInfo.specialRequests,
      };

      navigation.navigate('WakeDetailsInput', {
        wakeDetailsData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('WakeDetailsInput');
    }
  };

  const handleDelete = () => {
    if (wakeDetailsInfo) {
      Alert.alert(
        'Delete Wake Details',
        'Are you sure you want to delete this wake details? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await wakeDetailsService.deleteWakeDetails(wakeDetailsInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await wakeDetailsService.getWakeDetails();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('wake_details');
                }

                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting wake details info:', error);
                Alert.alert('Error', 'Failed to delete wake details');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Wake Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Wake Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {wakeDetailsInfo ? (
            <>
              {wakeDetailsInfo.wakeType && (
                <InfoItem label="Wake Type" value={wakeDetailsInfo.wakeType} />
              )}
              {wakeDetailsInfo.wakeLocationPreference !== undefined && (
                <InfoItem label="Specific Wake Location" value={wakeDetailsInfo.wakeLocationPreference} />
              )}
              {wakeDetailsInfo.wakeLocation && (
                <InfoItem label="Wake Location" value={wakeDetailsInfo.wakeLocation} />
              )}
              {wakeDetailsInfo.casketDisplayPreference !== undefined && (
                <InfoItem label="Casket Display Preference" value={wakeDetailsInfo.casketDisplayPreference} />
              )}
              {wakeDetailsInfo.casketDisplay && (
                <InfoItem label="Casket Display Details" value={wakeDetailsInfo.casketDisplay} />
              )}
              {wakeDetailsInfo.shareMemories !== undefined && (
                <InfoItem label="Share Memories" value={wakeDetailsInfo.shareMemories} />
              )}
              {wakeDetailsInfo.favoriteOutfitPreference !== undefined && (
                <InfoItem label="Display Favorite Outfit" value={wakeDetailsInfo.favoriteOutfitPreference} />
              )}
              {wakeDetailsInfo.favoriteOutfit && (
                <InfoItem label="Favorite Outfit Details" value={wakeDetailsInfo.favoriteOutfit} />
              )}
              {wakeDetailsInfo.favoritePhotosPreference !== undefined && (
                <InfoItem label="Display Favorite Photos" value={wakeDetailsInfo.favoritePhotosPreference} />
              )}
              {wakeDetailsInfo.favoritePhotos && (
                <InfoItem label="Favorite Photos Details" value={wakeDetailsInfo.favoritePhotos} />
              )}
              {wakeDetailsInfo.favoriteArtPreference !== undefined && (
                <InfoItem label="Display Favorite Art" value={wakeDetailsInfo.favoriteArtPreference} />
              )}
              {wakeDetailsInfo.favoriteArt && (
                <InfoItem label="Favorite Art Details" value={wakeDetailsInfo.favoriteArt} />
              )}
              {wakeDetailsInfo.favoriteVideosPreference !== undefined && (
                <InfoItem label="Display Favorite Videos" value={wakeDetailsInfo.favoriteVideosPreference} />
              )}
              {wakeDetailsInfo.favoriteVideos && (
                <InfoItem label="Favorite Videos Details" value={wakeDetailsInfo.favoriteVideos} />
              )}
              {wakeDetailsInfo.readingsPreference !== undefined && (
                <InfoItem label="Include Readings" value={wakeDetailsInfo.readingsPreference} />
              )}
              {wakeDetailsInfo.readingsName && (
                <InfoItem label="Readings Details" value={wakeDetailsInfo.readingsName} />
              )}
              {wakeDetailsInfo.specialRequests && (
                <InfoItem label="Special Requests" value={wakeDetailsInfo.specialRequests} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No wake details found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Wake Details</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default WakeDetailsDataScreen;